export interface RuntimeConfig {
  apiBaseUrl: string;
  cognitoRegion: string;
  cognitoClientId: string;
  cognitoDomain?: string;
  cognitoRedirectUri?: string;
  cognitoScopes: string;
  inlineUploadLimitBytes: number;
  forcePresignedUploads: boolean;
}

type RawRuntimeConfig = Partial<{
  API_BASE_URL: string;
  COGNITO_REGION: string;
  COGNITO_CLIENT_ID: string;
  COGNITO_DOMAIN: string;
  COGNITO_REDIRECT_URI: string;
  COGNITO_SCOPES: string;
  INLINE_UPLOAD_LIMIT_BYTES: number | string;
  FORCE_PRESIGNED_UPLOADS: boolean | string;
}>;

declare global {
  interface Window {
    __APP_CONFIG__?: RawRuntimeConfig;
    __APP_CONFIG_LOADED__?: boolean;
  }
}

function readBoolean(value: boolean | string | undefined, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    return ['1', 'true', 'yes', 'on'].includes(value.trim().toLowerCase());
  }
  return fallback;
}

function readNumber(value: number | string | undefined, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function readString(value: string | undefined, fallback = ''): string {
  return typeof value === 'string' ? value.trim() : fallback;
}

export async function loadRuntimeConfig(src = '/runtime-config.js'): Promise<void> {
  if (typeof window === 'undefined' || window.__APP_CONFIG_LOADED__) {
    return;
  }

  await new Promise<void>((resolve) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-runtime-config="${src}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => resolve(), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.dataset.runtimeConfig = src;
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });

  window.__APP_CONFIG_LOADED__ = true;
}

export function getRuntimeConfig(): RuntimeConfig {
  const raw = typeof window === 'undefined' ? {} : (window.__APP_CONFIG__ ?? {});
  return {
    apiBaseUrl: readString(raw.API_BASE_URL, 'http://localhost:8000'),
    cognitoRegion: readString(raw.COGNITO_REGION, 'ap-northeast-2'),
    cognitoClientId: readString(raw.COGNITO_CLIENT_ID),
    cognitoDomain: readString(raw.COGNITO_DOMAIN) || undefined,
    cognitoRedirectUri: readString(raw.COGNITO_REDIRECT_URI) || undefined,
    cognitoScopes: readString(raw.COGNITO_SCOPES, 'openid email profile'),
    inlineUploadLimitBytes: readNumber(raw.INLINE_UPLOAD_LIMIT_BYTES, 10 * 1024 * 1024),
    forcePresignedUploads: readBoolean(raw.FORCE_PRESIGNED_UPLOADS, false),
  };
}
